import React, { useMemo, useEffect, useState } from "react";

// tiny helper to keep demo “random” but stable per load id
const seeded = (seed) => {
  let s = String(seed)
    .split("")
    .reduce((a, c) => a + c.charCodeAt(0), 0);
  return () => (s = (s * 9301 + 49297) % 233280) / 233280;
};

const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];
const fmtPct = (n) => `${(n * 100).toFixed(0)}%`;

export default function AISidebarDemo({ bookedLoads = [], onAdopt }) {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [refreshTick, setRefreshTick] = useState(0);

  // “Recompute” every 11s to look alive
  useEffect(() => {
    const t = setInterval(() => {
      setRefreshTick((x) => x + 1);
      setLastUpdated(new Date());
    }, 11000);
    return () => clearInterval(t);
  }, []);

  const insights = useMemo(() => {
    const rng = seeded(refreshTick); // changes slightly each tick

    const loads = (Array.isArray(bookedLoads) ? bookedLoads : []).slice(0, 50);
    const miles = loads.map((l) => l?.distance ?? 0);
    const rpms = loads.map((l) => l?.rate_per_mile_est ?? 0);
    const revs = loads.map(
      (l) => (l?.distance ?? 0) * (l?.rate_per_mile_est ?? 0),
    );

    const sum = (a) => a.reduce((x, y) => x + (Number(y) || 0), 0);
    const avg = (a) => (a.length ? sum(a) / a.length : 0);
    const maxIdx = (a) =>
      a.length ? a.reduce((m, _, i) => (a[i] > a[m] ? i : m), 0) : -1;

    const bestRpmIdx = maxIdx(rpms);
    const bestRevIdx = maxIdx(revs);

    // Fake risk / ETA / match score
    const annotate = (l) => {
      const r = seeded(l?.id || l?.entry_id || Math.random())();
      const risk = r < 0.15 ? "High" : r < 0.45 ? "Medium" : "Low";
      const etaHrs = Math.max(
        6,
        Math.round(((l?.distance || 300) / 55) * (0.85 + r * 0.5)),
      );
      const match = 0.6 + r * 0.4; // 60–100%
      return { risk, etaHrs, match };
    };

    const top3 = loads
      .map((l) => ({
        l,
        score:
          (l?.rate_per_mile_est || 0) *
          (l?.distance || 0) *
          (0.8 + rng() * 0.4),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(({ l }) => ({ ...l, ...annotate(l) }));

    const issue = pick(rng, [
      "Deadhead risk from last drop-off is elevated in the Midwest.",
      "Driver availability dips Tue–Wed; suggest weekend pickups.",
      "RPM pressure easing on reefers; vans holding steady.",
      "Two brokers repeatedly slow-paying—flag for credit check.",
    ]);

    return {
      kpis: {
        loads: loads.length,
        avgRpm: avg(rpms),
        avgMiles: avg(miles),
        totalRev: sum(revs),
      },
      spotlight: {
        bestRpm:
          bestRpmIdx >= 0
            ? { ...loads[bestRpmIdx], ...annotate(loads[bestRpmIdx]) }
            : null,
        bestRev:
          bestRevIdx >= 0
            ? { ...loads[bestRevIdx], ...annotate(loads[bestRevIdx]) }
            : null,
      },
      recommendations: top3,
      issue,
    };
  }, [bookedLoads, refreshTick]);

  const Stat = ({ label, value, sub }) => (
    <div
      style={{
        padding: "10px 12px",
        border: "1px solid var(--chrome)",
        borderRadius: 8,
        background: "var(--surface)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{sub}</div>
      )}
    </div>
  );

  const Row = ({ load }) => {
    const route = `${load?.origin_city ?? "—"}, ${load?.origin_state ?? ""} → ${load?.destination_city ?? "—"}, ${load?.destination_state ?? ""}`;
    const total = (load?.distance || 0) * (load?.rate_per_mile_est || 0);
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 8,
          alignItems: "center",
          borderBottom: "1px dashed var(--chrome)",
          padding: "8px 0",
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>{route}</div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {Math.round(load?.distance || 0).toLocaleString()} mi · $
            {(load?.rate_per_mile_est || 0).toFixed(2)}/mi · $
            {Math.round(total).toLocaleString()}
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            <span
              style={{
                padding: "2px 6px",
                borderRadius: 4,
                background: "var(--chrome)",
                marginRight: 6,
              }}
            >
              ETA {load?.etaHrs ?? "?"}h
            </span>
            <span
              style={{
                padding: "2px 6px",
                borderRadius: 4,
                background: "var(--chrome)",
                marginRight: 6,
              }}
            >
              Risk {load?.risk}
            </span>
            <span
              style={{
                padding: "2px 6px",
                borderRadius: 4,
                background: "var(--accent-green-subtle)",
              }}
            >
              Match {fmtPct(load?.match ?? 0.8)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const k = insights.kpis;
  const s = insights.spotlight;

  return (
    <aside
      style={{
        width: 360,
        minWidth: 320,
        borderLeft: "1px solid var(--chrome)",
        padding: "16px",
        position: "sticky",
        top: 0,
        height: "calc(100vh - 80px)",
        overflowY: "auto",
        background: "var(--surface)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>AI Planner</h3>
        <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
          Updated {lastUpdated.toLocaleTimeString()}
        </div>
      </div>

      {/* KPIs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Stat label="Booked Loads" value={k.loads} />
        <Stat label="Avg RPM" value={`$${(k.avgRpm || 0).toFixed(2)}`} />
        <Stat
          label="Avg Miles"
          value={Math.round(k.avgMiles || 0).toLocaleString()}
        />
        <Stat
          label="Total Revenue"
          value={`$${Math.round(k.totalRev || 0).toLocaleString()}`}
        />
      </div>

      {/* Spotlights */}
      {s.bestRpm && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-secondary)",
              marginBottom: 6,
            }}
          >
            Highest RPM
          </div>
          <Row load={s.bestRpm} />
        </div>
      )}
      {s.bestRev && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-secondary)",
              marginBottom: 6,
            }}
          >
            Top Revenue
          </div>
          <Row load={s.bestRev} />
        </div>
      )}

      {/* Recommendations */}
      <div style={{ marginTop: 6, marginBottom: 10, fontWeight: 600 }}>
        Suggested Next Assignments
      </div>
      <div>
        {insights.recommendations.map((l, i) => (
          <Row key={(l?.id || l?.entry_id || i) + "rec"} load={l} />
        ))}
      </div>

      {/* Issue banner */}
      <div
        style={{
          marginTop: 16,
          padding: "10px 12px",
          border: "1px solid var(--warning)",
          background: "rgba(255,193,7,0.08)",
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        <i className="fas fa-lightbulb" style={{ marginRight: 8 }} />
        {insights.issue}
      </div>
    </aside>
  );
}
